import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Gift, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { HeroPanel } from "../components/ui-extras";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useSeo from "../lib/useSeo";
import { useTranslation } from "react-i18next";

const PENDING_KEY = "costock_pending_referral";

/**
 * /r/:handle — the investor's referral destination.
 *
 * Renders logged-out (that's the whole point), and stashes the handle so the
 * attribution still lands after the user signs up and comes back. Attribution is
 * first-touch and permanent, enforced server-side.
 */
export default function ReferralLanding() {
  const { t } = useTranslation();
  const { handle } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useSeo({ title: info?.display_name, description: info?.headline, noindex: true });

  useEffect(() => {
    api.resolveReferral(handle)
      .then((r) => {
        setInfo(r.data);
        localStorage.setItem(PENDING_KEY, handle);
      })
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, [handle]);

  useEffect(() => {
    // Already signed in? Attribute immediately and send them to the profile.
    if (user && info) {
      api.claimReferral(handle)
        .then(() => { localStorage.removeItem(PENDING_KEY); toast.success(t("referral.claimed")); })
        .catch(() => {})
        .finally(() => navigate(`/investors/${info.investor_id}`, { replace: true }));
    }
  }, [user, info, handle, navigate, t]);

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-brand-500" size={28} /></div>;
  if (!info) return <div className="min-h-screen grid place-items-center text-slate-400">{t("referral.invalid")}</div>;

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <HeroPanel
        eyebrow={t("referral.eyebrow")}
        title={t("referral.title", { name: info.display_name })}
        subtitle={info.headline || t("referral.fallbackSub")}
        testid="referral-hero"
      >
        {info.credit_days > 0 && (
          <div className="inline-flex items-center gap-2 rounded-xl bg-white/15 px-4 py-2.5 text-sm">
            <Gift size={16} /> {t("referral.credit", { days: info.credit_days })}
          </div>
        )}
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Link to="/login" className="btn-invert justify-center"
                data-testid="referral-signup">
            {t("referral.cta")} <ArrowRight size={16} />
          </Link>
          <Link to="/leaderboard" className="btn-outline border-white/40 text-white hover:bg-white/10 justify-center">
            {t("referral.browse")}
          </Link>
        </div>
      </HeroPanel>
    </div>
  );
}

/** Claims a stashed referral once the user finally signs in. */
export function claimPendingReferral() {
  const pending = localStorage.getItem(PENDING_KEY);
  if (!pending) return;
  api.claimReferral(pending).catch(() => {}).finally(() => localStorage.removeItem(PENDING_KEY));
}
